import { OutOfBandTransactionStatus } from "@modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import {
  type OutOfBandTransactionDetailDto,
  type OutOfBandTransactionDto,
} from "@modules/finances/boxPaymentAtCounter/gateway/outOfBandTransactionDto"
import { type BoxFinanceApiGateway } from "@modules/finances/gateway/boxFinanceApiGateway"
import { type OutOfBandCustomerHistoryDto } from "@modules/finances/gateway/customerHistoryDto"
import { type FinanceStatisticsDto } from "@modules/finances/gateway/financeStatisticsDto"
import { TaxSettingsStatus } from "@modules/finances/gateway/taxesDto"
import { type TransactionDetailsDto } from "@modules/finances/gateway/transactionDto"
import { type TransactionTotalsDto } from "@modules/finances/gateway/transactionTotalsDto"
import { PaymentPlace, TransactionStatus } from "@modules/finances/types/financesType"
import { type StripeErrorsDto } from "@modules/finances/types/stripeErrorTypes"
import { type Page } from "@modules/utils/paginated"

// Reusable fake data constants
const FAKE_FINANCE_STATISTICS: FinanceStatisticsDto = {
  counts: {
    succeededCount: 0,
    pendingCount: 0,
    pendingSepaDebitCount: 0,
    failedCount: 0,
    disputedCount: 0,
    refundedCount: 0,
    atCounterCount: 0,
    onlineCount: 0,
  },
  stats: {
    netTotal: 0,
    outOfBandTotal: 0,
    feesTotal: 0,
    refundTotal: 0,
    sepaDebitAmountTotal: 0,
    currency: "EUR",
    activeCustomers: 0,
    currentBalance: {
      pending: 0,
      available: 0,
    },
    lastPayout: null,
    payoutTotalAmount: 0,
  },
  firstPeriod: "",
  isMissingDocumentsError: false,
  toValidate: {
    failed: {
      count: 0,
      total: 0,
    },
    pending: {
      count: 0,
      total: 0,
    },
    disputed: {
      count: 0,
      total: 0,
    },
  },
}

const FAKE_TRANSACTION_TOTALS: TransactionTotalsDto = {
  succeededTransactionsNumber: 125,
  pendingTransactionsNumber: 15,
  failedTransactionsNumber: 8,
  disputedTransactionsNumber: 2,
  refundedTransactionsNumber: 5,
}

const FAKE_OUT_OF_BAND_TRANSACTION_DETAIL: OutOfBandTransactionDetailDto = {
  invoiceId: "inv_123456789",
  date: "2025-09-15T10:30:00Z",
  user: {
    id: "user_123",
    boxMemberNumber: "BM12345",
    firstname: "John",
    lastname: "Doe",
    username: "johndoe",
    avatar: null,
    address: {
      address: "123 Main St",
      additionalInformation: null,
      postalCode: "12345",
      city: "New York",
      country: "US",
    },
    email: "john.doe@example.com",
    nationality: "US",
    phoneNumber: {
      number: "5551234567",
      countryCode: "+1",
    },
    stripeCustomerId: "cus_123456789",
  },
  box: null,
  amount: 99.99,
  netAmount: 95.0,
  totalTaxAmount: 4.99,
  fee: 2.5,
  currency: "EUR",
  status: OutOfBandTransactionStatus.CASH,
  invoiceNumber: "INV-2025-001",
  invoicePdfUrl: "https://example.com/invoices/INV-2025-001.pdf",
  lines: [
    {
      description: "Monthly Subscription",
      netAmount: 95.0,
      taxAmount: 4.99,
      totalAmount: 99.99,
      taxPercent: 5.25,
      quantity: 1,
      currency: "EUR",
      periodStart: "2025-09-01T00:00:00Z",
      periodEnd: "2025-09-30T23:59:59Z",
    },
  ],
  discount: null,
}

export class FakeBoxFinanceApi implements BoxFinanceApiGateway {
  async getTransactions(
    _filter: TransactionStatus | undefined,
    _paymentPlace: PaymentPlace | undefined,
    _nameFilter: string | undefined,
    _period: string,
    _page?: number,
    _size = 10,
  ) {
    return Promise.resolve({
      content: [],
      page: 1,
      totalSize: 0,
      totalPages: 1,
    })
  }
  async getTransactionsHistory(_stripeCustomerId: string, _page?: string, size = 10) {
    return Promise.resolve({
      data: [],
      page: 1,
      pageSize: size,
      total: 0,
      hasMore: false,
    })
  }

  getTransaction(_transactionId: string): Promise<TransactionDetailsDto> {
    return Promise.resolve({} as TransactionDetailsDto)
  }

  refundTransaction(_transactionId: string): Promise<void> {
    return Promise.resolve()
  }

  getStats(_period: string): Promise<FinanceStatisticsDto> {
    return Promise.resolve(FAKE_FINANCE_STATISTICS)
  }

  getTransactionTotals(_period: string): Promise<TransactionTotalsDto> {
    return Promise.resolve(FAKE_TRANSACTION_TOTALS)
  }

  getLastUpdate(): Promise<string> {
    return Promise.resolve("")
  }

  getFirstPeriod(): Promise<string> {
    return Promise.resolve("")
  }

  updateStats(): Promise<FinanceStatisticsDto> {
    return Promise.resolve(FAKE_FINANCE_STATISTICS)
  }

  exportTransactions(_startDate: string, _endDate: string): Promise<string> {
    return Promise.resolve("")
  }

  exportMonthlyInvoices(_yearMonth: string): Promise<{ blob: Blob; filename: string | undefined }> {
    return Promise.resolve({
      blob: new Blob(),
      filename: "monthly_invoices.zip",
    })
  }

  exportPayoutTransactions(_payoutId: string): Promise<string> {
    return Promise.resolve("")
  }

  getStripeAccountErrors(): Promise<StripeErrorsDto | null> {
    return Promise.resolve(null)
  }

  getOverdueTransactions(_page: number, _size = 20): Promise<Page<OutOfBandTransactionDto>> {
    return Promise.resolve({
      content: [],
      page: 1,
      totalSize: 0,
      totalPages: 1,
    })
  }

  getOutOfBandTransactions(_period: string, _page: number, _size = 20): Promise<Page<OutOfBandTransactionDto>> {
    return Promise.resolve({
      content: [],
      page: 1,
      totalSize: 0,
      totalPages: 1,
    })
  }

  getOutOfBandTransaction(_invoiceId: string): Promise<OutOfBandTransactionDetailDto> {
    return Promise.resolve(FAKE_OUT_OF_BAND_TRANSACTION_DETAIL)
  }

  validateOutOfBandTransaction(
    _invoiceId: string,
    _newStatus: OutOfBandTransactionStatus,
  ): Promise<OutOfBandTransactionDetailDto> {
    return Promise.resolve(FAKE_OUT_OF_BAND_TRANSACTION_DETAIL)
  }

  removeFromOverdueList(_invoiceId: string): Promise<OutOfBandTransactionDetailDto> {
    return Promise.resolve(FAKE_OUT_OF_BAND_TRANSACTION_DETAIL)
  }

  getOutOfBandCustomerHistory(
    _stripeCustomerId: string,
    _page: number,
    _size = 20,
  ): Promise<Page<OutOfBandCustomerHistoryDto>> {
    return Promise.resolve({
      content: [],
      page: 1,
      totalSize: 0,
      totalPages: 1,
    })
  }

  activateTaxes(): Promise<TaxSettingsStatus> {
    return Promise.resolve(TaxSettingsStatus.ACTIVE)
  }

  getTaxesRegistrationToken(): Promise<string> {
    return Promise.resolve("token")
  }

  getTransactionPdf(_transactionId: string): Promise<Blob> {
    return Promise.resolve(new Blob())
  }

  getOutofBandTransactionPdf(_invoiceId: string): Promise<Blob> {
    return Promise.resolve(new Blob())
  }
}
